import React from "react";
import { Fab } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { motion, useScroll } from "framer-motion";

const MotionFab = motion(Fab);

export default function ScrollToTopButton({
  threshold = 250,
  bottom = 35,
  right = 20,
}) {
  const { scrollY } = useScroll();
  const [show, setShow] = React.useState(false);

  const SHOW_AT = threshold;
  const HIDE_AT = Math.max(0, threshold - 80);

  React.useEffect(() => {
    let ticking = false;
    const unsub = scrollY.on("change", (y) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShow((prev) => (prev ? y > HIDE_AT : y > SHOW_AT));
        ticking = false;
      });
    });
    return () => unsub();
  }, [scrollY, SHOW_AT, HIDE_AT]);

  const goTop = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 300);
  };

  const openWhatsAppChat = () => {
    const phoneNumber = "7027338781"; // Replace with the desired phone number
    const message = encodeURIComponent("Hello, I need assistance."); // Replace with your custom message
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, "_blank"); // Open WhatsApp chat in a new tab
  };

  return (
    <motion.div
      layout
      style={{
        position: "fixed",
        bottom: `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`,
        right: `calc(${right}px + env(safe-area-inset-right, 0px))`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        zIndex: 1500,
      }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      {/* WhatsApp Button */}
      <MotionFab
        layout
        onClick={openWhatsAppChat}
        aria-label="WhatsApp"
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        sx={{
          background: "#21bd5bff ",
          color: "white",
          transition: "all 0.2s ease",
          "&:hover": { background: "#25D366", scale: 1.2 },
        }}
      >
        <WhatsAppIcon sx={{ fontSize: "35px" }} />
      </MotionFab>

      {/* Scroll-to-top button with animated height */}
      {show && (
        <MotionFab
          layout
          onClick={goTop}
          aria-label="Scroll to top"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={
            show
              ? { opacity: 1, y: 0, scale: 1, height: 56 }
              : { opacity: 0, y: 16, scale: 0.96, height: 0 }
          }
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 25,
            mass: 0.7,
          }}
          sx={{
            background: "#136273",
            color: "white",
            boxShadow: 10,
            border: "2px solid white",
            overflow: "hidden", // prevent layout jump
            transition: "all 0.2s ease",
            "&:hover": {
              background: "white",
              color: "#136273",
              boxShadow: "0px 10px 24px rgba(0,0,0,0.25)",
              transform: "scale(1.08)",
              border: "2px solid #136273",
            },
            "&:active": {
              scale: 0.8,
            },
          }}
        >
          <ArrowUpwardIcon />
        </MotionFab>
      )}
    </motion.div>
  );
}
